import { routeSeoSeed } from '@/config/route-seo-seed';

export type RouteNextStepLink = {
  path: string;
  reason: string;
  cta?: string;
  label?: string;
};

export type RouteNextStepsConfig = {
  title?: string;
  intro: string;
  links: RouteNextStepLink[];
};

const CALCULATOR_CTA = 'Open calculator';
const GUIDE_CTA = 'Read guide';
const RESOURCE_CTA = 'Open resource';

function normalizeCanonicalPath(pathname: string): string {
  if (!pathname || pathname === '/index' || pathname === '/index.html') {
    return '/';
  }

  if (pathname === '/') {
    return pathname;
  }

  const trimmed = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const withoutHtml = trimmed.endsWith('.html') ? trimmed.slice(0, -'.html'.length) : trimmed;
  const withoutTrailingSlash = withoutHtml.endsWith('/') && withoutHtml !== '/' ? withoutHtml.slice(0, -1) : withoutHtml;

  return withoutTrailingSlash || '/';
}

function createConfig(intro: string, links: RouteNextStepLink[]): RouteNextStepsConfig {
  return {
    intro,
    links
  };
}

const calculatorNextSteps: Record<string, RouteNextStepsConfig> = {
  '/': createConfig('Use the FIRE calculator as your baseline, then compare adjacent planning routes to test whether a different lever changes the outcome more.', [
    {
      path: '/advanced-calculator',
      reason: 'Stress-test withdrawal sequencing, taxes, and longevity risk after you estimate your FIRE timeline.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/coast-fire-calculator',
      reason: 'Check whether your current portfolio can compound toward retirement without additional contributions.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/retirement-goal-calculator',
      reason: 'Backsolve the monthly savings needed when your target date or portfolio goal is still fuzzy.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/compound-interest',
      reason: 'Model how contributions and compounding shift the timeline before committing to a savings rate.',
      cta: CALCULATOR_CTA
    }
  ]),
  '/advanced-calculator': createConfig('Use the advanced FIRE calculator when you need more than a single target number. It is most useful after you have a baseline and want to pressure-test the plan.', [
    {
      path: '/',
      reason: 'Compare the advanced drawdown model against the simpler FIRE baseline so the extra complexity stays purposeful.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/retirement-goal-calculator',
      reason: 'Backsolve the savings rate needed to hit a specific target after stress-testing the drawdown.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/faq',
      reason: 'Review the methodology answers if you want a quick explanation of the assumptions behind the model.',
      cta: RESOURCE_CTA
    },
    {
      path: '/glossary',
      reason: 'Look up withdrawal and tax terms so the outputs stay easy to interpret.',
      cta: RESOURCE_CTA
    }
  ]),
  '/coast-fire-calculator': createConfig('Use Coast FIRE when you want to know whether compounding alone can carry the plan from here. The next step is usually a direct comparison with your target and savings capacity.', [
    {
      path: '/',
      reason: 'Compare the Coast FIRE milestone with the broader FIRE timeline so you can decide whether current contributions are enough.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/retirement-goal-calculator',
      reason: 'Backsolve the recurring savings needed if your coast milestone is still too far away.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/compound-interest',
      reason: 'See how different contribution and return assumptions change the path toward your coast milestone.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/glossary',
      reason: 'Review Coast FIRE and compounding terms before comparing assumptions.',
      cta: RESOURCE_CTA
    }
  ]),
  '/compound-interest': createConfig('Use the compound interest calculator when you want a clean view of growth mechanics. From there, move into goal-setting or spending planning.', [
    {
      path: '/retirement-goal-calculator',
      reason: 'Translate the growth curve into a concrete savings target and timeline.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/budget-planner',
      reason: 'Check whether the contribution path you want is actually sustainable from monthly cash flow.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/coast-fire-calculator',
      reason: 'See whether compounding alone gets you to a retirement milestone without more contributions.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/guides/investing-basics',
      reason: 'Read the investing basics guide if you want the growth model translated into practical portfolio habits.',
      cta: GUIDE_CTA
    }
  ]),
  '/retirement-goal-calculator': createConfig('Use the retirement goal calculator when you have a target date but need the savings math behind it. The best next step is usually either a cash-flow check or a growth comparison.', [
    {
      path: '/',
      reason: 'Compare your backsolved savings target against a full FIRE timeline estimate.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/compound-interest',
      reason: 'Test whether higher contributions or a longer timeline matter more for your goal.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/budget-planner',
      reason: 'Check whether the required monthly contribution fits your actual budget.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/guides/investing-basics',
      reason: 'Review investing basics if you want the target turned into a repeatable plan.',
      cta: GUIDE_CTA
    }
  ]),
  '/budget-planner': createConfig('Use the budget planner to turn a broad savings idea into a monthly cash-flow plan. Once the budget is clear, the next step is usually a debt, paycheck, or housing decision.', [
    {
      path: '/paycheck-calculator',
      reason: 'See how much of each paycheck is actually available for savings and spending.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/debt-payoff',
      reason: 'Compare debt payoff strategies before committing extra cash to investing.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/emergency-fund',
      reason: 'Confirm whether your cash reserve should come first before aggressive investing.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/guides/debt-payoff-strategies',
      reason: 'Use the debt payoff guide to choose a strategy that fits your monthly budget.',
      cta: GUIDE_CTA
    }
  ]),
  '/debt-payoff': createConfig('Use the debt payoff calculator when you want a cleaner tradeoff between speed, interest cost, and monthly breathing room. The next step is usually either budgeting or income planning.', [
    {
      path: '/budget-planner',
      reason: 'Check how much extra payment room you actually have after essentials and savings.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/paycheck-calculator',
      reason: 'Translate annual pay into take-home cash so payoff assumptions stay realistic.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/emergency-fund',
      reason: 'Make sure your debt plan is not crowding out the reserve you still need.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/guides/debt-payoff-strategies',
      reason: 'Review payoff frameworks if you want a methodical next move after comparing scenarios.',
      cta: GUIDE_CTA
    }
  ]),
  '/emergency-fund': createConfig('Use the emergency fund calculator to size your safety buffer before you optimize for returns. The best follow-up is usually budget, income, or debt planning.', [
    {
      path: '/budget-planner',
      reason: 'Check whether your monthly budget can support the reserve target you want.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/paycheck-calculator',
      reason: 'See how much room each paycheck leaves for building the reserve.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/debt-payoff',
      reason: 'Balance emergency savings against debt reduction if you are deciding where surplus cash should go first.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/faq',
      reason: 'Check the FAQ for guidance on how Fire-Calc frames conservative planning choices.',
      cta: RESOURCE_CTA
    }
  ]),
  '/mortgage-calculator': createConfig('Use the mortgage calculator when you need the full carrying cost, not just the sticker price. The next step is usually a housing comparison or affordability check.', [
    {
      path: '/rent-vs-buy',
      reason: 'Compare ownership against renting before you commit to a monthly payment.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/budget-planner',
      reason: 'Check whether the payment fits the rest of your monthly plan.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/guides/first-time-homebuyer',
      reason: 'Use the first-time homebuyer guide to think through down payment, credit, and closing costs.',
      cta: GUIDE_CTA
    },
    {
      path: '/faq',
      reason: 'Review the FAQ if you want quick answers on methodology and common assumptions.',
      cta: RESOURCE_CTA
    }
  ]),
  '/paycheck-calculator': createConfig('Use the paycheck calculator when you need to turn gross pay into real spending power. That usually leads straight into a budget or savings decision.', [
    {
      path: '/budget-planner',
      reason: 'Map take-home pay into a monthly plan with explicit savings and expense buckets.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/debt-payoff',
      reason: 'See whether debt reduction should come before more aggressive investing.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/guides/investing-basics',
      reason: 'Turn pre-tax and after-tax choices into a clearer investing order.',
      cta: GUIDE_CTA
    },
    {
      path: '/glossary',
      reason: 'Look up tax and account terms so paycheck deductions are easier to interpret.',
      cta: RESOURCE_CTA
    }
  ]),
  '/job-offer-comparison': createConfig('Use the job offer comparison calculator when equity, taxes, and vesting mean the headline total comp number hides the real ranking. The best next step is usually a paycheck, budget, or FIRE follow-up once you have picked an offer.', [
    {
      path: '/paycheck-calculator',
      reason: 'Translate the accepted offer into take-home pay so monthly planning reflects real cash flow.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/budget-planner',
      reason: 'Map take-home pay into a sustainable monthly plan with explicit savings and spending buckets.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/',
      reason: 'Use the FIRE calculator to test how the accepted offer affects your long-term retirement timeline.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/retirement-goal-calculator',
      reason: 'Backsolve the savings rate you need at the new compensation level to hit your retirement target.',
      cta: CALCULATOR_CTA
    }
  ]),
  '/equity-calculator': createConfig('Use the equity calculator to see what your RSUs, options, ESPP, and other grants are really worth over time and after tax. The best next step is usually to compare offers, translate vested shares into cash-flow planning, or pressure-test your long-term timeline.', [
    {
      path: '/job-offer-comparison',
      reason: 'Comparing offers? Score competing packages side by side with full equity, tax, and liquidity modeling.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/paycheck-calculator',
      reason: 'Translate salary plus vesting RSUs into take-home pay so monthly planning reflects real cash flow.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/',
      reason: 'Fold projected equity proceeds into your FIRE timeline to see how they move your retirement date.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/glossary',
      reason: 'Look up RSU, ISO, NSO, ESPP, QSBS, and 83(b) terms used throughout the equity model.',
      cta: RESOURCE_CTA
    }
  ]),
  '/rent-vs-buy': createConfig('Use the rent-vs-buy calculator when you want a structured housing decision instead of a rule of thumb. The best next step is usually to compare the mortgage, budget, and first-home guidance routes.', [
    {
      path: '/mortgage-calculator',
      reason: 'Estimate the full ownership payment before deciding whether buying makes sense.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/budget-planner',
      reason: 'Make sure the housing choice still leaves room for savings and reserves.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/guides/first-time-homebuyer',
      reason: 'Use the first-time homebuyer guide to pressure-test affordability and readiness.',
      cta: GUIDE_CTA
    },
    {
      path: '/faq',
      reason: 'Check the FAQ if you want a quick explanation of assumptions behind the comparison.',
      cta: RESOURCE_CTA
    }
  ])
};

const contentNextSteps: Record<string, RouteNextStepsConfig> = {
  '/guides': createConfig('Use the guide hub to choose a focused playbook, then jump into the calculators that turn it into a decision.', [
    {
      path: '/guides/first-time-homebuyer',
      reason: 'Start with the guide that helps when housing is the decision in front of you.',
      cta: GUIDE_CTA
    },
    {
      path: '/guides/investing-basics',
      reason: 'Read the investing guide if you want a simpler long-term wealth-building workflow.',
      cta: GUIDE_CTA
    },
    {
      path: '/guides/debt-payoff-strategies',
      reason: 'Use the debt payoff guide if your next move is about freeing up monthly cash flow.',
      cta: GUIDE_CTA
    },
    {
      path: '/glossary',
      reason: 'Keep the glossary open as a reference while you work through the guides.',
      cta: RESOURCE_CTA
    }
  ]),
  '/faq': createConfig('Use the FAQ when you want quick answers before you commit to a calculator or guide. It is a good bridge into the rest of the site.', [
    {
      path: '/',
      reason: 'Go back to the FIRE calculator if you are ready to model a timeline again.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/advanced-calculator',
      reason: 'Open the advanced FIRE calculator when your question is really about drawdown or longevity risk.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/glossary',
      reason: 'Review the glossary when a term or assumption needs a cleaner definition.',
      cta: RESOURCE_CTA
    },
    {
      path: '/guides',
      reason: 'Move into a guide when the answer you need is more procedural than mathematical.',
      cta: GUIDE_CTA
    }
  ]),
  '/contact': createConfig('Use the contact page when you need to reach the team directly. Many questions are answered faster by the FAQ or About page first.', [
    {
      path: '/faq',
      reason: 'Check the FAQ — most methodology and usage questions are already answered there.',
      cta: RESOURCE_CTA
    },
    {
      path: '/about-us',
      reason: 'Read about how Fire-Calc is built and maintained before getting in touch.',
      cta: RESOURCE_CTA
    },
    {
      path: '/privacy-policy',
      reason: 'Review how we handle the email and data you share when you contact us.',
      cta: RESOURCE_CTA
    },
    {
      path: '/',
      reason: 'Return to the FIRE calculator to keep planning while you wait for a reply.',
      cta: CALCULATOR_CTA
    }
  ]),
  '/about-us': createConfig('Use the about page to understand how Fire-Calc is built, then follow the links that explain methodology or product changes.', [
    {
      path: '/faq',
      reason: 'Read the FAQ for a faster explanation of assumptions and model behavior.',
      cta: RESOURCE_CTA
    },
    {
      path: '/changelog',
      reason: 'Check the changelog to see what changed most recently across the site.',
      cta: RESOURCE_CTA
    },
    {
      path: '/guides',
      reason: 'Jump back into the guide hub once you are ready to turn context into action.',
      cta: GUIDE_CTA
    },
    {
      path: '/',
      reason: 'Return to the FIRE calculator if you want to test the planning approach immediately.',
      cta: CALCULATOR_CTA
    }
  ]),
  '/glossary': createConfig('Use the glossary as a reference layer while you move through the calculators and guides. It is most useful when you want to keep terminology consistent across pages.', [
    {
      path: '/',
      reason: 'Open the FIRE calculator when you are ready to apply a term in a real planning model.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/advanced-calculator',
      reason: 'Use the advanced FIRE calculator for withdrawal and tax terms that need more detail.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/guides',
      reason: 'Read a guide if you want the terms turned into a planning workflow.',
      cta: GUIDE_CTA
    },
    {
      path: '/faq',
      reason: 'Use the FAQ for broader methodology questions that go beyond a single definition.',
      cta: RESOURCE_CTA
    }
  ]),
  '/changelog': createConfig('Use the changelog to track what changed in the product and how those changes affect the planning experience.', [
    {
      path: '/about-us',
      reason: 'See how the site philosophy and product story connect to the changes listed here.',
      cta: RESOURCE_CTA
    },
    {
      path: '/faq',
      reason: 'Use the FAQ if a change raises methodology or usage questions.',
      cta: RESOURCE_CTA
    },
    {
      path: '/guides',
      reason: 'Move back to the guides when you are ready to use the updated tools in a planning workflow.',
      cta: GUIDE_CTA
    },
    {
      path: '/',
      reason: 'Open the FIRE calculator to try the latest product changes in a live model.',
      cta: CALCULATOR_CTA
    }
  ]),
  '/privacy-policy': createConfig('Use the privacy policy to understand how the site handles data, then return to the planning pages that matter to you.', [
    {
      path: '/about-us',
      reason: 'Read about the product and planning philosophy behind the site.',
      cta: RESOURCE_CTA
    },
    {
      path: '/faq',
      reason: 'Check the FAQ for a quicker summary of how the calculators work.',
      cta: RESOURCE_CTA
    },
    {
      path: '/glossary',
      reason: 'Review the glossary if you want the planning terms used elsewhere on the site.',
      cta: RESOURCE_CTA
    },
    {
      path: '/',
      reason: 'Go back to the main FIRE calculator once you are comfortable with the data policy.',
      cta: CALCULATOR_CTA
    }
  ]),
  '/terms-of-service': createConfig('Use the terms page to review the site rules, then continue to the resource that matches your planning task.', [
    {
      path: '/about-us',
      reason: 'See the broader Fire-Calc mission and product philosophy.',
      cta: RESOURCE_CTA
    },
    {
      path: '/faq',
      reason: 'Use the FAQ for a quick summary of methodology and intended use.',
      cta: RESOURCE_CTA
    },
    {
      path: '/guides',
      reason: 'Read a guide when you are ready to turn the site into an actual planning workflow.',
      cta: GUIDE_CTA
    },
    {
      path: '/',
      reason: 'Return to the FIRE calculator once you are ready to model a scenario.',
      cta: CALCULATOR_CTA
    }
  ])
};

const guideDetailNextSteps: Record<string, RouteNextStepsConfig> = {
  '/guides/first-time-homebuyer': createConfig('Use this guide when housing is the decision in front of you. The best follow-up is usually a calculator that makes affordability and break-even timing visible.', [
    {
      path: '/mortgage-calculator',
      reason: 'Estimate monthly payment and total borrowing cost before you make an offer.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/rent-vs-buy',
      reason: 'Compare renting and buying under your actual assumptions instead of relying on a rule of thumb.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/budget-planner',
      reason: 'Check whether the home decision fits the rest of your monthly plan.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/faq',
      reason: 'Review methodology questions before you lock in assumptions.',
      cta: RESOURCE_CTA
    }
  ]),
  '/guides/investing-basics': createConfig('Use this guide when you want the simplest durable investing setup. The next step is usually a calculator that translates habits into long-range outcomes.', [
    {
      path: '/',
      reason: 'See how investing habits affect a FIRE timeline in a longer-horizon model.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/compound-interest',
      reason: 'Model how consistent contributions and compounding work together over time.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/coast-fire-calculator',
      reason: 'Check whether your current investing pace is enough to coast toward retirement.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/glossary',
      reason: 'Use the glossary to keep investing terms and account types straight as you plan.',
      cta: RESOURCE_CTA
    }
  ]),
  '/guides/debt-payoff-strategies': createConfig('Use this guide when freeing up monthly cash flow is the priority. Once the method is clear, compare it against the calculators that show payoff speed and budget impact.', [
    {
      path: '/debt-payoff',
      reason: 'Compare avalanche, snowball, and balance-first strategies in the calculator.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/budget-planner',
      reason: 'See how the payoff plan fits alongside rent, groceries, and savings.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/paycheck-calculator',
      reason: 'Translate gross pay into the actual cash you can direct toward debt.',
      cta: CALCULATOR_CTA
    },
    {
      path: '/faq',
      reason: 'Review the FAQ if you want the payoff advice framed more broadly.',
      cta: RESOURCE_CTA
    }
  ])
};

const staticRouteNextSteps: Record<string, RouteNextStepsConfig> = {
  ...calculatorNextSteps,
  ...contentNextSteps,
  ...guideDetailNextSteps
};

export const routeNextStepsByCanonicalPath: Record<string, RouteNextStepsConfig> = {
  ...staticRouteNextSteps
};

const validCanonicalPaths = new Set(routeSeoSeed.map((route) => normalizeCanonicalPath(route.path)));

function assert(condition: unknown, message: string): asserts condition {
  if (!condition) {
    throw new Error(message);
  }
}

for (const [canonicalPath, config] of Object.entries(routeNextStepsByCanonicalPath)) {
  assert(validCanonicalPaths.has(canonicalPath), `Next-step config references unknown canonical route: ${canonicalPath}`);
  assert(config.links.length > 0 && config.links.length <= 4, `Next-step config for ${canonicalPath} must contain 1-4 links.`);

  const seenLinkPaths = new Set<string>();

  for (const link of config.links) {
    const normalizedLinkPath = normalizeCanonicalPath(link.path);

    assert(validCanonicalPaths.has(normalizedLinkPath), `Next-step config for ${canonicalPath} references unknown path: ${normalizedLinkPath}`);
    assert(normalizedLinkPath !== canonicalPath, `Next-step config for ${canonicalPath} cannot link to itself.`);
    assert(!seenLinkPaths.has(normalizedLinkPath), `Next-step config for ${canonicalPath} contains duplicate path: ${normalizedLinkPath}`);

    seenLinkPaths.add(normalizedLinkPath);
  }
}

export function getRouteNextSteps(canonicalPath: string): RouteNextStepsConfig | null {
  const normalized = normalizeCanonicalPath(canonicalPath);
  return routeNextStepsByCanonicalPath[normalized] ?? null;
}
